import { isRecord } from '@/shared/utils/apiResponse';

import type {
  AssinaturaDaTela,
  AssinaturaSimulada,
  ErroDaSimulacao,
  ResultadoDaAssinatura,
  SituacaoDaTelaDeAssinatura,
} from '../types';

/**
 * 404 é o link de outra conta ou execução, ou sem simulação atual; 403, a autorização perdida;
 * 410, o link que expirou. Nos três a tela explica sem mostrar o documento (TELA-002, Bloqueado).
 */
const STATUS_QUE_BLOQUEIAM = new Set([403, 404, 410]);

export const erroBloqueiaAssinatura = (erro: ErroDaSimulacao | null): boolean =>
  erro !== null && STATUS_QUE_BLOQUEIAM.has(erro.status);

export const situacaoDaTelaDeAssinatura = (tela: AssinaturaDaTela): SituacaoDaTelaDeAssinatura => {
  if (erroBloqueiaAssinatura(tela.erro) || erroBloqueiaAssinatura(tela.erroDoRegistro)) {
    return 'bloqueado';
  }
  if (!tela.dados) return tela.erro ? 'erro' : 'carregando';
  return tela.dados.estado === 'PENDENTE' ? 'pendente' : 'decidida';
};

const ORDEM_DAS_ACOES: ResultadoDaAssinatura[] = ['CONCLUIDA', 'CANCELADA', 'EXPIRADA', 'FALHA'];

const ESTADOS_DA_ASSINATURA = new Set<string>(['PENDENTE', ...ORDEM_DAS_ACOES]);

/**
 * A resposta do POST de resultado é a assinatura (200) ou o `{comandoId, estado}` do retry ainda
 * em curso (202): só a primeira forma substitui o que a tela mostra.
 */
export const ehAssinaturaSimulada = (valor: unknown): valor is AssinaturaSimulada =>
  isRecord(valor) &&
  typeof valor.id === 'string' &&
  typeof valor.nomeDocumento === 'string' &&
  typeof valor.estado === 'string' &&
  ESTADOS_DA_ASSINATURA.has(valor.estado) &&
  Array.isArray(valor.acoesPermitidas);

/** Ordem fixa na tela, só com o que o core permite para este provedor e este flow. */
export const acoesDaAssinatura = (assinatura: AssinaturaSimulada): ResultadoDaAssinatura[] =>
  ORDEM_DAS_ACOES.filter((acao) => assinatura.acoesPermitidas.includes(acao));

/** Um resultado em voo trava os outros; estado já decidido não aceita outro (CAS no core). */
export const podeRegistrarResultado = (
  tela: AssinaturaDaTela,
  resultado: ResultadoDaAssinatura,
): boolean =>
  tela.registrando === null &&
  tela.simulacaoId !== null &&
  tela.dados?.estado === 'PENDENTE' &&
  tela.dados.acoesPermitidas.includes(resultado);
